import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface PdfExportOptions {
  filename?: string;
  format?: 'a4' | 'letter' | 'legal';
  orientation?: 'portrait' | 'landscape';
  margin?: number;
  scale?: number;
  quality?: number;
  backgroundColor?: string;
  onProgress?: (progress: number) => void;
  onError?: (error: Error) => void;
}

export class EnhancedPdfExporter {
  private static readonly DEFAULT_OPTIONS: Required<PdfExportOptions> = {
    filename: 'document.pdf',
    format: 'a4',
    orientation: 'portrait',
    margin: 10,
    scale: 2,
    quality: 0.8, // Reduced for smaller file size
    backgroundColor: '#ffffff',
    onProgress: () => {},
    onError: (error) => console.error('PDF Export Error:', error),
  };

  /**
   * Enhanced PDF export with better quality, error handling, and multi-page support
   * Optimized for table handling and smaller file sizes
   */
  static async exportElementToPdf(selector: string | HTMLElement, options: PdfExportOptions = {}): Promise<void> {
    const config = { ...this.DEFAULT_OPTIONS, ...options };

    try {
      config.onProgress(5);

      // Get element
      const element = this.getElement(selector);
      if (!element) {
        throw new Error('Element not found for PDF export');
      }

      config.onProgress(10);

      // Prepare element for export with table handling
      await this.prepareElementForExport(element);

      config.onProgress(15);

      // Use web worker for PDF generation
      await this.exportWithWorker(element, config);
    } catch (error) {
      config.onError(error as Error);
      throw error;
    }
  }

  /**
   * Export multiple elements to a single PDF
   */
  static async exportMultipleElementsToPdf(
    selectors: (string | HTMLElement)[],
    options: PdfExportOptions = {},
  ): Promise<void> {
    const config = { ...this.DEFAULT_OPTIONS, ...options };

    try {
      const pdf = new jsPDF({
        orientation: config.orientation === 'portrait' ? 'p' : 'l',
        unit: 'mm',
        format: config.format,
      });

      let isFirstPage = true;

      for (let i = 0; i < selectors.length; i++) {
        const element = this.getElement(selectors[i]);
        if (!element) continue;

        config.onProgress((i / selectors.length) * 80);

        await this.prepareElementForExport(element);
        const canvas = await this.createHighQualityCanvas(element, config);

        if (!isFirstPage) {
          pdf.addPage();
        }

        await this.addCanvasToPdf(pdf, canvas, config);
        isFirstPage = false;
      }

      config.onProgress(90);
      pdf.save(config.filename);
      config.onProgress(100);
    } catch (error) {
      config.onError(error as Error);
      throw error;
    }
  }

  private static getElement(selector: string | HTMLElement): HTMLElement | null {
    if (typeof selector === 'string') {
      return document.querySelector(selector) as HTMLElement;
    }
    return selector;
  }

  private static async prepareElementForExport(element: HTMLElement): Promise<void> {
    // Ensure all images are loaded
    const images = element.querySelectorAll('img');
    const imagePromises = Array.from(images).map((img) => {
      return new Promise((resolve) => {
        if (img.complete) {
          resolve(img);
        } else {
          img.onload = () => resolve(img);
          img.onerror = () => resolve(img);
        }
      });
    });

    await Promise.all(imagePromises);

    // Handle table page breaks
    this.handleTablePageBreaks(element);

    // Force layout recalculation
    element.style.visibility = 'visible';
    element.style.display = 'block';

    // Wait for any CSS transitions/animations to complete
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  private static handleTablePageBreaks(element: HTMLElement): void {
    const tables = element.querySelectorAll('table');
    tables.forEach((table) => {
      // Add CSS to prevent table row splitting
      table.style.pageBreakInside = 'avoid';
      const rows = table.querySelectorAll('tr');
      rows.forEach((row) => {
        (row as HTMLElement).style.pageBreakInside = 'avoid';
      });
    });
  }

  private static async createHighQualityCanvas(
    element: HTMLElement,
    config: Required<PdfExportOptions>,
  ): Promise<HTMLCanvasElement> {
    const canvas = await html2canvas(element, {
      scale: config.scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: config.backgroundColor,
      removeContainer: true,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        // Ensure proper styling in cloned document
        const clonedElement = clonedDoc.querySelector(element.tagName) as HTMLElement;
        if (clonedElement) {
          clonedElement.style.transform = 'none';
          clonedElement.style.animation = 'none';
          // Ensure table styles are preserved
          const tables = clonedElement.querySelectorAll('table');
          tables.forEach((table) => {
            table.style.width = '100%';
            table.style.tableLayout = 'fixed';
          });
        }
      },
    });

    return canvas;
  }

  private static async exportWithWorker(element: HTMLElement, config: Required<PdfExportOptions>): Promise<void> {
    // For now, fall back to direct export since web worker has issues with DOM manipulation
    // Web workers cannot access DOM elements directly
    config.onProgress(20);

    const canvas = await this.createHighQualityCanvas(element, config);

    config.onProgress(60);

    const pdf = await this.generatePdfFromCanvas(canvas, config);

    config.onProgress(90);

    pdf.save(config.filename);

    config.onProgress(100);
  }

  private static async generatePdfFromCanvas(
    canvas: HTMLCanvasElement,
    config: Required<PdfExportOptions>,
  ): Promise<jsPDF> {
    const pdf = new jsPDF({
      orientation: config.orientation === 'portrait' ? 'p' : 'l',
      unit: 'mm',
      format: config.format,
    });

    await this.addCanvasToPdf(pdf, canvas, config);
    return pdf;
  }

  private static async addCanvasToPdf(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    config: Required<PdfExportOptions>,
  ): Promise<void> {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const contentWidth = pageWidth - config.margin * 2;
    const contentHeight = pageHeight - config.margin * 2;

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // If content fits on one page, just add it
    if (imgHeight <= contentHeight) {
      const imgData = canvas.toDataURL('image/jpeg', config.quality);
      pdf.addImage(imgData, 'JPEG', config.margin, config.margin, imgWidth, imgHeight);
      return;
    }

    // Table-aware page breaking: assume each table row is 80px high
    const ROW_HEIGHT_PX = 80;
    const scaleFactor = imgHeight / canvas.height; // mm per pixel
    const pageHeightPx = contentHeight / scaleFactor;

    // Calculate how many complete rows can fit per page
    const rowsPerPage = Math.floor(pageHeightPx / ROW_HEIGHT_PX);
    const availableHeightPx = rowsPerPage * ROW_HEIGHT_PX;

    let currentY = 0;
    let pageNum = 0;

    while (currentY < canvas.height) {
      if (pageNum > 0) {
        pdf.addPage();
      }

      // Calculate remaining content height
      const remainingPx = canvas.height - currentY;

      // If remaining content fits on current page, put it all there
      if (remainingPx <= availableHeightPx) {
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d')!;
        pageCanvas.width = canvas.width;
        pageCanvas.height = remainingPx;

        pageCtx.drawImage(canvas, 0, currentY, canvas.width, remainingPx, 0, 0, pageCanvas.width, pageCanvas.height);

        const pageImgData = pageCanvas.toDataURL('image/jpeg', config.quality);
        const pageImgHeight = remainingPx * scaleFactor;

        pdf.addImage(pageImgData, 'JPEG', config.margin, config.margin, imgWidth, pageImgHeight);
        break; // Done
      } else {
        // Fill the page with complete rows only
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d')!;
        pageCanvas.width = canvas.width;
        pageCanvas.height = availableHeightPx;

        pageCtx.drawImage(
          canvas,
          0,
          currentY,
          canvas.width,
          availableHeightPx,
          0,
          0,
          pageCanvas.width,
          pageCanvas.height,
        );

        const pageImgData = pageCanvas.toDataURL('image/jpeg', config.quality);
        pdf.addImage(pageImgData, 'JPEG', config.margin, config.margin, imgWidth, contentHeight);

        currentY += availableHeightPx;
        pageNum++;
      }
    }
  }

  /**
   * Utility method to get optimal PDF dimensions for an element
   */
  static getOptimalPdfDimensions(element: HTMLElement): {
    format: 'a4' | 'letter';
    orientation: 'portrait' | 'landscape';
  } {
    const rect = element.getBoundingClientRect();
    const aspectRatio = rect.width / rect.height;

    if (aspectRatio > 1.2) {
      return { format: 'a4', orientation: 'landscape' };
    } else {
      return { format: 'a4', orientation: 'portrait' };
    }
  }
}
